import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { getApiBaseUrl } from '@core/services/api-base-url';
import {
  CompanyNamedLookupResponse,
  CompanyOrganisationalLevelResponse,
  CompanyRequest,
  CompanyNamedLookupRequest,
  CreateCompanyOrganisationalLevelRequest,
  UpdateCompanyOrganisationalLevelRequest,
  CompanyResponse,
} from './company.model';

type CompanyQuery = {
  clientId?: string | null;
  includeInactive?: boolean | null;
};

type CompanyIdQuery = {
  companyId?: string | null;
  includeInactive?: boolean | null;
};

@Injectable({ providedIn: 'root' })
export class CompanyApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = getApiBaseUrl();

  getCompanies(query: CompanyQuery): Observable<CompanyResponse[]> {
    return this.http.get<CompanyResponse[]>(`${this.apiBaseUrl}companies`, {
      params: toQueryParams(query),
    });
  }

  getCompany(companyId: string): Observable<CompanyResponse> {
    return this.http.get<CompanyResponse>(`${this.apiBaseUrl}companies/${companyId}`);
  }

  createCompany(body: CompanyRequest): Observable<CompanyResponse> {
    return this.http.post<CompanyResponse>(`${this.apiBaseUrl}companies`, body);
  }

  updateCompany(companyId: string, body: CompanyRequest): Observable<CompanyResponse> {
    return this.http.put<CompanyResponse>(`${this.apiBaseUrl}companies/${companyId}`, body);
  }
  
  deactivateCompany(companyId: string): Observable<CompanyResponse> {
    return this.http.put<CompanyResponse>(`${this.apiBaseUrl}companies/${companyId}/deactivate`, {});
  }

  activateCompany(companyId: string): Observable<CompanyResponse> {
    return this.http.put<CompanyResponse>(`${this.apiBaseUrl}companies/${companyId}/activate`, {});
  }

  getCompanyDivisions(query: CompanyIdQuery): Observable<CompanyNamedLookupResponse[]> {
    return this.http.get<CompanyNamedLookupResponse[]>(`${this.apiBaseUrl}company-divisions`, {
      params: toQueryParams(query),
    });
  }

  getCompanyDivision(companyDivisionId: string): Observable<CompanyNamedLookupResponse> {
    return this.http.get<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-divisions/${companyDivisionId}`);
  }

  createCompanyDivision(body: CompanyNamedLookupRequest): Observable<CompanyNamedLookupResponse> {
    return this.http.post<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-divisions`, body);
  }

  updateCompanyDivision(companyDivisionId: string, body: CompanyNamedLookupRequest): Observable<CompanyNamedLookupResponse> {
    return this.http.put<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-divisions/${companyDivisionId}`, body);
  }

  deactivateCompanyDivision(companyDivisionId: string): Observable<CompanyNamedLookupResponse> {
    return this.http.put<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-divisions/${companyDivisionId}/deactivate`, {});
  }

  activateCompanyDivision(companyDivisionId: string): Observable<CompanyNamedLookupResponse> {
    return this.http.put<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-divisions/${companyDivisionId}/activate`, {});
  }

  getCompanyJobTitles(query: CompanyIdQuery): Observable<CompanyNamedLookupResponse[]> {
    return this.http.get<CompanyNamedLookupResponse[]>(`${this.apiBaseUrl}company-job-titles`, {
      params: toQueryParams(query),
    });
  }

  getCompanyJobTitle(companyJobTitleId: string): Observable<CompanyNamedLookupResponse> {
    return this.http.get<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-job-titles/${companyJobTitleId}`);
  }

  createCompanyJobTitle(body: CompanyNamedLookupRequest): Observable<CompanyNamedLookupResponse> {
    return this.http.post<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-job-titles`, body);
  }

  updateCompanyJobTitle(companyJobTitleId: string, body: CompanyNamedLookupRequest): Observable<CompanyNamedLookupResponse> {
    return this.http.put<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-job-titles/${companyJobTitleId}`, body);
  }

  deactivateCompanyJobTitle(companyJobTitleId: string): Observable<CompanyNamedLookupResponse> {
    return this.http.put<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-job-titles/${companyJobTitleId}/deactivate`, {});
  }

  activateCompanyJobTitle(companyJobTitleId: string): Observable<CompanyNamedLookupResponse> {
    return this.http.put<CompanyNamedLookupResponse>(`${this.apiBaseUrl}company-job-titles/${companyJobTitleId}/activate`, {});
  }

  getCompanyOrganisationalLevels(query: CompanyIdQuery): Observable<CompanyOrganisationalLevelResponse[]> {
    return this.http.get<CompanyOrganisationalLevelResponse[]>(`${this.apiBaseUrl}company-organisational-levels`, {
      params: toQueryParams(query),
    });
  }

  getCompanyOrganisationalLevel(companyOrganisationalLevelId: string): Observable<CompanyOrganisationalLevelResponse> {
    return this.http.get<CompanyOrganisationalLevelResponse>(`${this.apiBaseUrl}company-organisational-levels/${companyOrganisationalLevelId}`);
  }

  createCompanyOrganisationalLevel(body: CreateCompanyOrganisationalLevelRequest): Observable<CompanyOrganisationalLevelResponse> {
    return this.http.post<CompanyOrganisationalLevelResponse>(`${this.apiBaseUrl}company-organisational-levels`, body);
  }

  updateCompanyOrganisationalLevel(companyOrganisationalLevelId: string, body: UpdateCompanyOrganisationalLevelRequest): Observable<CompanyOrganisationalLevelResponse> {
    return this.http.put<CompanyOrganisationalLevelResponse>(`${this.apiBaseUrl}company-organisational-levels/${companyOrganisationalLevelId}`, body);
  }

  deactivateCompanyOrganisationalLevel(companyOrganisationalLevelId: string): Observable<CompanyOrganisationalLevelResponse> {
    return this.http.put<CompanyOrganisationalLevelResponse>(`${this.apiBaseUrl}company-organisational-levels/${companyOrganisationalLevelId}/deactivate`, {});
  }

  activateCompanyOrganisationalLevel(companyOrganisationalLevelId: string): Observable<CompanyOrganisationalLevelResponse> {
    return this.http.put<CompanyOrganisationalLevelResponse>(`${this.apiBaseUrl}company-organisational-levels/${companyOrganisationalLevelId}/activate`, {});
  }
}

function toQueryParams(query: Record<string, string | boolean | null | undefined>): HttpParams {
  let params = new HttpParams();

  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) {
      params = params.set(key, value.toString());
    }
  }

  return params;
}
